import React, { useState, useRef, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";

const getAny = (key) =>
  localStorage.getItem(key) ?? sessionStorage.getItem(key) ?? null;

export default function LandlordNavbar({
  mobile = false,
  open = false,
  onClose,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const mobileRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("User");

  const navItems = [
    { name: "Dashboard", href: "/dashboard" },
    { name: "Reservations", href: "/reservations" },
    { name: "Accounting", href: "/accounting" },
  ];

  useEffect(() => {
    const storedEmail = getAny("user_email");
    const storedUserRaw = getAny("user");
    let finalEmail = storedEmail;
    let finalName = "User";
    try {
      const userObj = storedUserRaw ? JSON.parse(storedUserRaw) : null;
      if (!finalEmail && userObj?.email) finalEmail = userObj.email;
      if (userObj?.name) finalName = userObj.name;
      else if (finalEmail) finalName = finalEmail.split("@")[0];
    } catch {}
    setUserEmail(finalEmail || "");
    setUserName(finalName || "User");
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!mobile || !open) return;
    const onDocClick = (e) => {
      if (mobileRef.current && !mobileRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [mobile, open, onClose]);

  const handleSignOut = () => {
    [localStorage, sessionStorage].forEach((s) => s.clear());
    navigate("/landlord-signin");
  };

  const isActive = (href) =>
    location.pathname === href ||
    (href !== "/" && location.pathname.startsWith(href));

  return (
    <>
      {mobile && open && (
        <div
          onClick={() => onClose?.()}
          className="fixed inset-0 z-[9] bg-black/30 backdrop-blur-sm transition-opacity"
          aria-hidden="true"
        />
      )}

      <nav
        className={`w-full bg-white border-b border-blue-100 shadow-sm z-10 ${
          mobile && open
            ? "fixed top-16 left-0 right-0 h-[calc(100vh-4rem)] shadow-lg border-t rounded-b-2xl transition-transform"
            : mobile
            ? "hidden"
            : "fixed top-0 left-0 right-0"
        }`}
        aria-label="Landlord navigation"
      >
        <div
          ref={mobile ? mobileRef : undefined}
          onClick={mobile ? (e) => e.stopPropagation() : undefined}
          className={`relative z-10 mx-auto max-w-7xl ${
            mobile ? "h-full flex flex-col" : "px-4 sm:px-6 lg:px-8"
          }`}
        >
          {/* 🖥️ Desktop */}
          {!mobile && (
            <div className="flex justify-end items-center gap-8 h-16 relative">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative text-sm font-medium transition-all duration-300 ${
                    isActive(item.href)
                      ? "text-blue-700 font-semibold"
                      : "text-gray-700 hover:text-blue-700"
                  }`}
                >
                  {item.name}
                  {isActive(item.href) && (
                    <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-blue-600 rounded-full" />
                  )}
                </Link>
              ))}

              {/* Avatar dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen((s) => !s)}
                  className="w-9 h-9 rounded-full bg-gradient-to-r from-blue-200 via-blue-100 to-blue-200 text-blue-800 flex items-center justify-center font-semibold cursor-pointer shadow hover:scale-105 transition-transform"
                  aria-haspopup="menu"
                  aria-expanded={dropdownOpen}
                >
                  {userName.charAt(0).toUpperCase()}
                </button>

                {dropdownOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-3 w-64 bg-white border border-blue-100 shadow-xl rounded-xl overflow-hidden z-50 animate-fadeIn"
                  >
                    <div className="px-4 py-3 border-b border-blue-100">
                      <p className="text-sm font-medium text-gray-900">
                        {userName}
                      </p>
                      <p className="text-xs text-gray-600">{userEmail}</p>
                    </div>
                    <Link
                      to="/settings"
                      onClick={() => setDropdownOpen(false)}
                      className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50"
                      role="menuitem"
                    >
                      Settings
                    </Link>
                    <div className="px-4 py-3 border-y border-blue-100">
                      <p className="text-sm font-medium text-gray-900">
                        Contact Us
                      </p>
                      <p className="text-xs text-blue-700">
                        support@yourspotrented.com
                      </p>
                    </div>
                    <button
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-blue-50"
                      role="menuitem"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 📱 Mobile */}
          {mobile && (
            <div className="flex-1 flex flex-col justify-between p-6 overflow-y-auto">
              <div className="space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => onClose?.()}
                    className={`block text-base sm:text-lg font-medium rounded-xl px-3 py-2 transition ${
                      isActive(item.href)
                        ? "text-blue-800 bg-blue-100"
                        : "text-gray-700 hover:text-blue-800 hover:bg-blue-50"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              <div className="border-t border-blue-100 pt-4 mt-6 space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {userName}
                  </p>
                  <p className="text-xs text-gray-600 break-all">{userEmail}</p>
                </div>
                <Link
                  to="/settings"
                  onClick={() => onClose?.()}
                  className="block text-sm text-gray-700 hover:text-blue-800 hover:underline"
                >
                  Settings
                </Link>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Contact Us
                  </p>
                  <a
                    href="mailto:support@yourspotrented.com"
                    className="text-xs text-blue-700 hover:underline"
                  >
                    support@yourspotrented.com
                  </a>
                </div>
                <button
                  onClick={() => {
                    handleSignOut();
                    onClose?.();
                  }}
                  className="w-full text-left text-sm text-red-600 hover:underline"
                >
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}
